import CheckIcon from "@mui/icons-material/Check";
import {
  alpha,
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteValue,
  Chip,
  TextField,
  autocompleteClasses,
  styled,
} from "@mui/material";
import { SyntheticEvent, useEffect, useState } from "react";

export interface Availability {
  sportCourtTaxId: string;
  rented: boolean;
  value: number;
  timeFromTo: string;
  label: string;
  rentalTime: number;
}

const StyledAutocomplete = styled(
  Autocomplete<Availability, true, false, false>
)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 14,
    minHeight: 54,
    alignItems: "flex-start",
    paddingTop: 6,
    paddingBottom: 6,
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.background.default, 0.28)
        : theme.palette.background.paper,
    transition: "background-color 0.2s ease, border-color 0.2s ease",
  },
  "& .MuiInputBase-input": {
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: 2,
    borderColor: theme.palette.divider,
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
  },
  [`& .${autocompleteClasses.option}`]: {
    borderRadius: 10,
    margin: "4px 8px",
    minHeight: 42,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    "&.Mui-focused": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

interface CustomProps {
  id: string;
  availability: Availability[];
  label: string;
  fullWidth?: boolean;
  onChange?: (
    event: SyntheticEvent,
    value: AutocompleteValue<Availability, true, false, false>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<Availability>
  ) => void;
  error: boolean;
  helperText?: React.ReactNode;
  disabled: boolean;
  initialValues?: Availability[];
}

export default function MultiScheduleSelect({
  id,
  availability,
  label,
  onChange,
  error,
  helperText,
  disabled,
  initialValues,
}: CustomProps) {
  const [selectedValues, setSelectedValues] = useState<Availability[]>(
    initialValues ?? []
  );

  useEffect(() => {
    setSelectedValues(initialValues ?? []);
  }, [initialValues]);

  const handleChange = (
    event: SyntheticEvent,
    value: Availability[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<Availability>
  ) => {
    setSelectedValues(value);
    onChange?.(event, value, reason, details);
  };

  return (
    <StyledAutocomplete
      id={id}
      multiple
      disableCloseOnSelect
      disabled={disabled}
      options={availability}
      value={selectedValues}
      onChange={handleChange}
      noOptionsText="Nenhum horário disponível"
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) =>
        option.timeFromTo === value.timeFromTo &&
        option.sportCourtTaxId === value.sportCourtTaxId
      }
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <span style={{ flexGrow: 1 }}>{option.label}</span>
          {selected ? <CheckIcon fontSize="small" /> : null}
        </li>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const tagProps = getTagProps({ index });
          return (
            <Chip
              {...tagProps}
              key={`${option.sportCourtTaxId}-${option.timeFromTo}`}
              label={option.timeFromTo}
              sx={{ borderRadius: "10px" }}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          helperText={helperText}
          placeholder={selectedValues.length === 0 ? "Selecione horários" : ""}
        />
      )}
    />
  );
}
